import { useState, useEffect } from 'react'
import type {
  CSVExporter,
  MarketplaceType,
  ExportResult,
  ValidationResult,
} from '../contracts/csvExporter'
import type { Batch } from '../contracts/storageService'
import { AppIcons } from './icons'

interface ExportManagerProps {
  csvExporter: CSVExporter
  batch?: Batch | null
  images?: any[]
  imageMetadata?: Record<string, any>
  selectedMarketplace: MarketplaceType
  sessionName?: string
}

export function ExportManager({ csvExporter, batch, images, imageMetadata, selectedMarketplace, sessionName }: ExportManagerProps) {
  const [isExporting, setIsExporting] = useState(false)
  const [exportResult, setExportResult] = useState<ExportResult | null>(null)
  const [validation, setValidation] = useState<ValidationResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const marketplaceRequirements = csvExporter.getMarketplaceRequirements(selectedMarketplace)

  // Determine if we're using batch or direct images approach
  const hasData = batch ? batch.images?.length > 0 : (images?.length || 0) > 0
  const dataCount = batch ? (batch.images?.length || 0) : (images?.length || 0)

  // Auto-validate on mount and when data/marketplace changes
  useEffect(() => {
    if (batch) {
      // Use batch validation
      try {
        const validationResult = csvExporter.validateExport(batch, selectedMarketplace)
        setValidation(validationResult)
        setError(null)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Validation failed')
        setValidation(null)
      }
    } else if (images && imageMetadata) {
      // Create temporary batch for validation when using direct images
      const tempBatch = {
        id: 'temp-validation',
        name: sessionName || 'Current Session',
        createdAt: new Date(),
        images: images.map(image => ({
          id: image.id,
          file: image.file,
          metadata: imageMetadata[image.id]
        }))
      }

      try {
        const validationResult = csvExporter.validateExport(tempBatch, selectedMarketplace)
        setValidation(validationResult)
        setError(null)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Validation failed')
        setValidation(null)
      }
    }
  }, [batch, images, imageMetadata, selectedMarketplace, csvExporter, sessionName])

  const handleExport = async () => {
    try {
      setIsExporting(true)
      setError(null)

      let result: ExportResult

      if (batch) {
        // Use batch export
        console.log('🔍 [ExportManager] Using batch export method')
        result = await csvExporter.exportToMarketplace(batch, selectedMarketplace)
      } else if (images && imageMetadata) {
        // Use direct images export
        console.log('🔍 [ExportManager] Using direct images export method')
        console.log('  - Images:', images)
        console.log('  - Image metadata:', imageMetadata)
        result = await csvExporter.exportFromImages(images, imageMetadata, selectedMarketplace, sessionName)
      } else {
        setError('No data available for export')
        return
      }

      setExportResult(result)
    } catch (err) {
      console.error('🔍 [ExportManager] Export failed:', err)
      setError(err instanceof Error ? err.message : 'Export failed')
    } finally {
      setIsExporting(false)
    }
  }

  const handleDownload = async () => {
    if (!exportResult) return

    try {
      await csvExporter.downloadExport(exportResult)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed')
    }
  }

  if (!hasData) {
    return (
      <div className="w-full bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="text-center py-8 text-gray-500">
          <svg
            className="w-12 h-12 mx-auto mb-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <p>No data available for export</p>
          <p className="text-sm">Upload images and generate metadata to export</p>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full bg-white rounded-xl shadow-sm border border-gray-200">
      {/* Header */}
      <div className="border-b border-gray-200 px-4 lg:px-6 py-4 rounded-t-xl">
        <h2 className="text-lg lg:text-xl font-semibold text-gray-900">Export to {selectedMarketplace.charAt(0).toUpperCase() + selectedMarketplace.slice(1)}</h2>
        <p className="text-sm text-gray-600 mt-1">
          {batch
            ? `Exporting batch: ${batch.name} (${dataCount} images)`
            : `Exporting session: ${sessionName || 'Current Session'} (${dataCount} images)`
          }
        </p>
      </div>

      <div className="p-4 space-y-4">
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        {/* Validation Status */}
        {validation && (
          <div className={`p-4 rounded-xl ${
            validation.isValid ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
          }`}>
            <h4 className={`font-medium mb-2 ${
              validation.isValid ? 'text-green-800' : 'text-red-800'
            }`}>
              <span className="flex items-center">
                {validation.isValid ? (
                  <>
                    <AppIcons.CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                    Ready to Export
                  </>
                ) : (
                  <>
                    <AppIcons.X className="w-4 h-4 mr-2 text-red-500" />
                    Export Validation Failed
                  </>
                )}
              </span>
            </h4>

            {validation.errors.length > 0 && (
              <div className="mb-3">
                <p className="text-sm font-medium text-red-700 mb-1">Issues that need to be fixed:</p>
                {validation.errors.map((error, index) => (
                  <p key={index} className="text-sm text-red-600">• {error.message}</p>
                ))}
              </div>
            )}

            {validation.warnings.length > 0 && (
              <div>
                <p className="text-sm font-medium text-yellow-700 mb-1">Warnings:</p>
                {validation.warnings.map((warning, index) => (
                  <p key={index} className="text-sm text-yellow-600">• {warning.message}</p>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Export Actions */}
        {validation && validation.isValid && (
          <div className="flex justify-center lg:justify-end">
            <button
              onClick={async () => {
                try {
                  setIsExporting(true)
                  setError(null)

                  let result: ExportResult

                  // Handle both batch and direct images export
                  if (batch) {
                    console.log('🔍 [ExportManager] Exporting batch:', batch.name)
                    result = await csvExporter.exportToMarketplace(batch, selectedMarketplace)
                  } else if (images && imageMetadata) {
                    console.log('🔍 [ExportManager] Exporting direct images:', images.length)
                    result = await csvExporter.exportFromImages(images, imageMetadata, selectedMarketplace, sessionName)
                  } else {
                    throw new Error('No data available for export')
                  }

                  // Download the CSV file
                  await csvExporter.downloadExport(result)
                } catch (err) {
                  console.error('🔍 [ExportManager] Export failed:', err)
                  setError(err instanceof Error ? err.message : 'Export failed')
                } finally {
                  setIsExporting(false)
                }
              }}
              disabled={isExporting}
              className="bg-green-600 text-white px-6 py-3 rounded-xl hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center w-full lg:w-auto lg:min-w-[200px] h-12"
            >
              {isExporting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                  Downloading...
                </>
              ) : (
                <>
                  <AppIcons.Download className="w-4 h-4 mr-2" />
                  Download CSV File
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
